import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
    TextField,
    MenuItem,
    Button,
    Card,
    CardContent,
    Typography,
    IconButton,
    Box,
    Grid,
    TextareaAutosize,
    Autocomplete,
    Paper,
} from "@mui/material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import styled from "@emotion/styled";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import CloseIcon from "@mui/icons-material/Close";
import ImageIcon from "@mui/icons-material/Image";
import { useLocation } from "react-router-dom";
import {
  createOperatori,
  fetchOperatori,
  singleOperatori,
  updateOperatoriData,
} from "../../../features/operatori/operatoriThunk";
import { useDispatch, useSelector } from "react-redux";
import { selectSingleOperatori } from "../../../features/operatori/operatoriSlice";
const EmptyTextarea = ({ placeholder }) => {
  const Textarea = styled(TextareaAutosize)(
    ({ theme }) => `
      box-sizing: border-box;
      width: 100%;
      font-family: 'IBM Plex Sans', sans-serif;
      font-size: 0.875rem;
      font-weight: 400;
      line-height: 1.5;
      padding: 12px;
      border-radius: 5px;
      color: #160A2A;
      background: ${"#fff"};
      border: 2px solid #e5e5e5;
    `
  );

  return (
    <Textarea
      aria-label="empty textarea"
      placeholder={placeholder}
      minRows={4}
    />
  );
};

const schema = yup.object().shape({
  nome: yup.string().required("Il nome è obbligatorio"),
  cognome: yup.string().required("Il cognome è obbligatorio"),
  // role_id: yup.string().required("Il role_id è obbligatorio"),
  email: yup
    .string()
    .email("Inserisci un'email valida")
    .required("L'email è obbligatoria"),
  abilitazioni: yup.string().required("Seleziona un'abilitazione"),
  file: yup
    .mixed()
    .test("fileSize", "Il file è troppo grande (max 5MB)", (value) => {
      if (!value || value.length === 0) return true;
      return value[0].size <= 5 * 1024 * 1024;
    })
    .test("fileType", "Formato file non valido", (value) => {
      if (!value || value.length === 0) return true;
      return [
        "application/pdf",
        "application/msword",
        "image/jpeg",
        "image/png",
      ].includes(value[0].type);
    }),
});

const options = [
    "Formazione Sicurezza Generale (D.Lgs. 81/08)",
    "Formazione Sicurezza Specifica (rischio basso/medio/alto)",
    "Formazione Addetti Antincendio",
    "Formazione Primo Soccorso",
    "Patentino uso prodotti fitosanitari",
    "Corso per uso trattori e mezzi agricoli",
    "Formazione uso DPI",
    "Formazione HACCP (Igiene alimentare)",
    "Corso da cantiniere / enotecnico",
    "Corso su attrezzature enologiche",
    "Formazione su macchine imbottigliatrici",
    "Patentino per carrello elevatore (muletti)",
    "Corso assaggiatori ONAV / AIS",
    "Corso etichettatura e normativa DOC/DOCG",
    "Sommelier AIS o Fisar",
    "IAP (qualifica di imprenditore agricolo professionale)."
];

const FilePreview = ({ file }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [fileType, setFileType] = useState(null);

  useEffect(() => {
    if (!file) return;

    // Handle URL strings (existing files from backend)
    if (typeof file === "string") {
      setPreviewUrl(file);
      // Try to guess the file type from URL
      if (file.match(/\.(jpeg|jpg|png|gif|bmp|webp)$/i)) {
        setFileType("image");
      } else if (file.match(/\.(pdf)$/i)) {
        setFileType("pdf");
      } else if (file.match(/\.(doc|docx)$/i)) {
        setFileType("doc");
      } else if (file.match(/\.(txt|text)$/i)) {
        setFileType("text");
      } else {
        setFileType("other");
      }
      return;
    }

    // Handle File objects
    if (file instanceof File) {
      if (file.type.startsWith("image/")) {
        setPreviewUrl(URL.createObjectURL(file));
        setFileType("image");
      } else if (file.type === "application/pdf") {
        setPreviewUrl(URL.createObjectURL(file));
        setFileType("pdf");
      } else if (
        file.type.includes("document") ||
        file.type.includes("msword")
      ) {
        setFileType("doc");
        setPreviewUrl(URL.createObjectURL(file));
      } else if (file.type === "text/plain") {
        setFileType("text");
        setPreviewUrl(URL.createObjectURL(file));
      } else {
        setFileType("other");
        setPreviewUrl(URL.createObjectURL(file));
      }
    }
  }, [file]);

  useEffect(() => {
    // Clean up object URLs to avoid memory leaks
    return () => {
      if (previewUrl && typeof file !== "string") {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl, file]);

  const openInNewTab = () => {
    if (previewUrl) {
      window.open(previewUrl, "_blank");
    }
  };

  if (!file) return null;

  return (
    <Paper elevation={1} sx={{ p: 2, mt: 1, mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        {fileType === "image" && <ImageIcon color="primary" />}
        {fileType === "pdf" && <PictureAsPdfIcon color="error" />}
        {fileType === "doc" && <InsertDriveFileIcon color="primary" />}
        {fileType === "text" && <TextSnippetIcon color="info" />}
        {fileType === "other" && <InsertDriveFileIcon color="action" />}

        {/* <Typography variant="body2" sx={{ ml: 1 }}>
                      {file instanceof File ? file.name : file.split('/').pop()}
                  </Typography> */}
      </Box>

      {fileType === "image" && previewUrl && (
        <Box
          sx={{
            mt: 1,
            textAlign: "center",
            cursor: "pointer",
            "&:hover": { opacity: 0.9 },
          }}
          onClick={openInNewTab}
        >
          <img
            src={previewUrl}
            alt="File Preview"
            style={{ maxWidth: "100%", maxHeight: "150px" }}
          />
        </Box>
      )}

      {fileType === "pdf" && previewUrl && (
        <Box
          sx={{
            mt: 1,
            height: "150px",
            width: "100%",
            overflow: "hidden",
            position: "relative",
            cursor: "pointer",
            "&:hover": { opacity: 0.95 },
          }}
          onClick={openInNewTab}
        >
          <iframe
            src={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=0`}
            title="PDF Preview"
            width="100%"
            height="100%"
            scrolling="no"
            style={{
              border: "none",
              overflow: "hidden",
              width: "100%",
              height: "100%",
            }}
          />
          {/* Invisible overlay for clickable area */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              zIndex: 1,
            }}
          />
        </Box>
      )}

      {(fileType === "doc" || fileType === "text" || fileType === "other") &&
        previewUrl && (
          <Box
            sx={{
              mt: 1,
              p: 2,
              bgcolor: "#f5f5f5",
              borderRadius: 1,
              cursor: "pointer",
              "&:hover": { bgcolor: "#e0e0e0" },
            }}
            onClick={openInNewTab}
          >
            {/* <Typography variant="body2" align="center">
                          Click to open file
                      </Typography> */}
          </Box>
        )}

      {(fileType === "doc" || fileType === "text" || fileType === "other") &&
        !previewUrl && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1, fontStyle: "italic" }}
          >
            Preview not available for this file type
          </Typography>
        )}
    </Paper>
  );
};

// added remove image with close button
// const FilePreview = ({ file, onRemove }) => {
//   const [previewUrl, setPreviewUrl] = useState(null);
//   const [fileType, setFileType] = useState(null);

//   useEffect(() => {
//     if (!file) return;

//     // Handle URL strings (existing files from backend)
//     if (typeof file === "string") {
//       setPreviewUrl(file);
//       // Try to guess the file type from URL
//       if (file.match(/\.(jpeg|jpg|png|gif|bmp|webp)$/i)) {
//         setFileType("image");
//       } else if (file.match(/\.(pdf)$/i)) {
//         setFileType("pdf");
//       } else if (file.match(/\.(doc|docx)$/i)) {
//         setFileType("doc");
//       } else if (file.match(/\.(txt|text)$/i)) {
//         setFileType("text");
//       } else {
//         setFileType("other");
//       }
//       return;
//     }

//     // Handle File objects
//     if (file instanceof File) {
//       if (file.type.startsWith("image/")) {
//         setPreviewUrl(URL.createObjectURL(file));
//         setFileType("image");
//       } else if (file.type === "application/pdf") {
//         setPreviewUrl(URL.createObjectURL(file));
//         setFileType("pdf");
//       } else if (
//         file.type.includes("document") ||
//         file.type.includes("msword")
//       ) {
//         setFileType("doc");
//         setPreviewUrl(URL.createObjectURL(file));
//       } else if (file.type === "text/plain") {
//         setFileType("text");
//         setPreviewUrl(URL.createObjectURL(file));
//       } else {
//         setFileType("other");
//         setPreviewUrl(URL.createObjectURL(file));
//       }
//     }
//   }, [file]);

//   useEffect(() => {
//     // Clean up object URLs to avoid memory leaks
//     return () => {
//       if (previewUrl && typeof file !== "string") {
//         URL.revokeObjectURL(previewUrl);
//       }
//     };
//   }, [previewUrl, file]);

//   const openInNewTab = () => {
//     if (previewUrl) {
//       window.open(previewUrl, "_blank");
//     }
//   };

//   if (!file) return null;

//   return (
//     <Paper elevation={1} sx={{ p: 2, mt: 1, mb: 2, position: 'relative' }}>
//       {/* Close button positioned at top-right */}
//       <IconButton
//         onClick={onRemove}
//         sx={{
//           position: 'absolute',
//           right: 8,
//           top: 8,
//           backgroundColor: 'rgba(255,255,255,0.7)',
//           '&:hover': {
//             backgroundColor: 'rgba(255,255,255,0.9)',
//           }
//         }}
//       >
//         <CloseIcon />
//       </IconButton>

//       <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
//         {fileType === "image" && <ImageIcon color="primary" />}
//         {fileType === "pdf" && <PictureAsPdfIcon color="error" />}
//         {fileType === "doc" && <InsertDriveFileIcon color="primary" />}
//         {fileType === "text" && <TextSnippetIcon color="info" />}
//         {fileType === "other" && <InsertDriveFileIcon color="action" />}
//       </Box>

//       {/* Rest of the preview code remains the same */}
//       {fileType === "image" && previewUrl && (
//         <Box
//           sx={{
//             mt: 1,
//             textAlign: "center",
//             cursor: "pointer",
//             "&:hover": { opacity: 0.9 },
//           }}
//           onClick={openInNewTab}
//         >
//           <img
//             src={previewUrl}
//             alt="File Preview"
//             style={{ maxWidth: "100%", maxHeight: "150px" }}
//           />
//         </Box>
//       )}

//       {fileType === "pdf" && previewUrl && (
//         <Box
//           sx={{
//             mt: 1,
//             height: "150px",
//             width: "100%",
//             overflow: "hidden",
//             position: "relative",
//             cursor: "pointer",
//             "&:hover": { opacity: 0.95 },
//           }}
//           onClick={openInNewTab}
//         >
//           <iframe
//             src={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=0`}
//             title="PDF Preview"
//             width="100%"
//             height="100%"
//             scrolling="no"
//             style={{
//               border: "none",
//               overflow: "hidden",
//               width: "100%",
//               height: "100%",
//             }}
//           />
//           <Box
//             sx={{
//               position: "absolute",
//               top: 0,
//               left: 0,
//               width: "100%",
//               height: "100%",
//               zIndex: 1,
//             }}
//           />
//         </Box>
//       )}

//       {(fileType === "doc" || fileType === "text" || fileType === "other") &&
//         previewUrl && (
//           <Box
//             sx={{
//               mt: 1,
//               p: 2,
//               bgcolor: "#f5f5f5",
//               borderRadius: 1,
//               cursor: "pointer",
//               "&:hover": { bgcolor: "#e0e0e0" },
//             }}
//             onClick={openInNewTab}
//           />
//         )}

//       {(fileType === "doc" || fileType === "text" || fileType === "other") &&
//         !previewUrl && (
//           <Typography
//             variant="body2"
//             color="text.secondary"
//             sx={{ mt: 1, fontStyle: "italic" }}
//           >
//             Preview not available for this file type
//           </Typography>
//         )}
//     </Paper>
//   );
// };

// Add this function to the OperatoriForm component

const OperatoriForm = ({ cantine, setCantine, singleData }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const operatoriData = useSelector(selectSingleOperatori);
  const [allegatiPreview, setAllegatiPreview] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [filePreview, setFilePreview] = useState(null);
  // const operatoreId = singleData?.id;
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      surname: "",
      email: "",
      qualification: "",
      upload_file: null,
    },
  });

  // for remove the image
  // const handleRemoveFile = () => {
  //   setValue("upload_file", null);
  //   setFilePreview(null);
  //   // Clear the file input value
  //   const fileInput = document.querySelector('input[type="file"]');
  //   if (fileInput) {
  //     fileInput.value = "";
  //   }
  // };

  // const onSubmit = (data) => {
  //     console.log(data);
  // };
  const selectedFile = watch("upload_file");

  useEffect(() => {
    if (selectedFile) {
      setFilePreview(selectedFile);
    }
  }, [selectedFile]);

  useEffect(() => {
    if (singleData && cantine) {
      setValue("nome", singleData?.name || "");
      setValue("cognome", singleData?.surname || "");
      setValue("role_id", singleData?.role_id || "");
      setValue("email", singleData?.email || "");
      setValue("abilitazioni", singleData?.user_details?.qualification || "");

      if (singleData?.user_details?.upload_file) {
        setValue("upload_file", singleData.user_details.upload_file || "");
      }
    }
  }, [singleData, setValue, cantine]);

  const handleFileChange = (e, onChange) => {
    const file = e.target.files[0];
    if (file) {
      onChange(file);
      setFilePreview(file);
    }
  };
  const onSubmit = async (data) => {
    const formData = new FormData();
    formData.append("company_id", localStorage.getItem("company_id"));
    formData.append("name", data.nome);
    formData.append("surname", data.cognome);
    formData.append("role_id", "OPERATOR");
    formData.append("email", data.email);
    formData.append("qualification", data.abilitazioni);

    if (data.upload_file instanceof File) {
      formData.append("upload_file", data.upload_file);
    }

    try {
      if (cantine) {
        const updateData = new FormData();
        updateData.append("name", data.nome);
        updateData.append("surname", data.cognome);
        updateData.append("role_id", "OPERATOR");
        updateData.append("email", data.email);
        updateData.append("qualification", data.abilitazioni);

        if (data.upload_file instanceof File) {
          updateData.append("upload_file", data.upload_file);
        }

        await dispatch(
          updateOperatoriData({ id: singleData?.id, data: updateData })
        ).unwrap();
        dispatch(singleOperatori(singleData?.id));
        setCantine(false);
        localStorage.removeItem("strucutreOperatori");
        // alert("Operatore aggiornato con successo!");
      } else {
        await dispatch(createOperatori(formData)).unwrap();
        dispatch(fetchOperatori());
        setCantine(false);
        reset({
          nome: "",
          cognome: "",
          role_id: "",
          email: "",
          abilitazioni: "",
          upload_file: null,
        });
        // alert("Operatore creato con successo!");
      }
    } catch (err) {
      console.error("Operation Error:", err);

      // Check if the error is about email already existing
      // if (err?.errors?.error === "The email has already been taken.") {
      //   toast.error("Errore: L'email è già stata registrata");
      // } else {
      //   toast.error("Errore: L'email è già stata registrata");
      // }
    }
  };

  return (
    <>
      <form className="AziendaFrom" onSubmit={handleSubmit(onSubmit)}>
        <div className="AziendaFrom__inner">
          <Controller
            defaultValue=""
            name="nome"
            control={control}
            render={({ field }) => (
              <TextField
                className="customTextBox"
                {...field}
                label="Nome"
                fullWidth
                margin="normal"
                error={!!errors.nome}
                helperText={errors.nome?.message}
                sx={{ mt: "10px" }}
              />
            )}
          />
          <Controller
            defaultValue=""
            name="cognome"
            control={control}
            render={({ field }) => (
              <TextField
                className="customTextBox"
                {...field}
                label="Cognome"
                fullWidth
                margin="normal"
                error={!!errors.cognome}
                helperText={errors.cognome?.message}
                sx={{ mt: "10px" }}
              />
            )}
          />
          {/* <Controller
                        name="role_id"
                        control={control}
                        render={({ field }) => (
                            <TextField className="customTextBox"
                                {...field}
                                label="Role"
                                fullWidth
                                margin="normal"
                                error={!!errors.role_id}
                                helperText={errors.role_id?.message}
                                sx={{ mt: "10px" }}
                            />
                        )}
                    /> */}
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                className="customTextBox"
                {...field}
                label="Email"
                fullWidth
                margin="normal"
                error={!!errors.email}
                helperText={errors.email?.message}
                sx={{ mt: "10px" }}
              />
            )}
          />
          <Controller
            defaultValue=""
            name="abilitazioni"
            control={control}
            render={({ field }) => (
              <Autocomplete
                {...field}
                options={options}
                value={field.value || ""}
                // onChange={(event, value) => field.onChange(value)}  // To handle selected value properly
                onChange={(event, value) => field.onChange(value || "")}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Abilitazioni"
                    className="customTextBox autoCompleteTextBox"
                    margin="normal"
                    fullWidth
                    error={!!errors.abilitazioni}
                    helperText={errors.abilitazioni?.message}
                  />
                )}
              />
            )}
          />

          <Controller
            name="upload_file"
            control={control}
            render={({ field: { value, onChange, ...field } }) => (
              <Grid container spacing={1}>
                <Grid size={8}>
                  <TextField
                    {...field}
                    value={
                      value instanceof File
                        ? value.name
                        : typeof value === "string"
                        ? value
                        : "No file uploaded"
                    }
                    className="customTextBox"
                    label="Carica allegati documanti"
                    fullWidth
                    margin="normal"
                    InputProps={{
                      readOnly: true,
                      // Always ensure there's a meaningful display value
                      value:
                        value instanceof File
                          ? value.name
                          : typeof value === "string" && value
                          ? value
                          : "",
                    }}
                  />
                </Grid>
                <Grid size={4}>
                  <Button
                    className="caricafile-label"
                    variant="outlined"
                    component="label"
                  >
                    Carica file
                    <input
                      type="file"
                      hidden
                      accept="image/*,.pdf,.doc,.docx"
                      onChange={(e) => handleFileChange(e, onChange)}
                    />
                  </Button>
                </Grid>
                {/* File Preview Section */}
                {filePreview && (
                  <Box sx={{ mt: 2 }}>
                    <FilePreview file={filePreview} />
                  </Box>
                )}

                {/* onRemove added for image remove */}
                {/* {filePreview && (
                                  <Box sx={{ mt: 2 }}>
                                    <FilePreview file={filePreview} onRemove={handleRemoveFile} />
                                  </Box>
                                )} */}
              </Grid>
            )}
          />

          <div className="btnPart1">
            <Button type="submit" variant="contained">
              Salva dati
            </Button>
          </div>
        </div>
      </form>
    </>
  );
};

export default OperatoriForm;
